'use client';

import { useSessionStorageState } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useMemo, useState } from 'react';
import { BaseLineList } from '@/app/components/icons/BaseLineList';
import { LoginWhite } from '@/app/components/icons/LoginWhite';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { ACTIVITY_PAGE_FROM_KEY, HOME_PAGE, isEnterpriseDistribution } from '@/app/constants';
import { useDevice, useGlobalModal, useIsScreen, useUserInfo } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { ActivityPageFromEnum } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { useLocale, usePathname, useRouter } from '@/i18n/routing';
import {
  getActivityImageUrl,
  HEADER_ACTIVITIES,
  type HeaderActivityConfig,
  shouldShowActivity,
} from './activity-utils';
import { LoggedInHeaderSection } from './components/LoggedInHeaderSection';
import { MobileMenu } from './components/MobileMenu';
import { usePageType } from './hooks/use-page-type';

interface HeaderProps {
  className?: string;
  sticky?: boolean;
}

export const Header: FC<HeaderProps> = ({ className, sticky = false }) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const { showLoginButton } = useUserInfo();
  const { isMobile, isLenovoMini } = useDevice();
  const { isMdScreen } = useIsScreen();

  const { userInfo, region } = useStore((state) => ({
    userInfo: state.userInfo,
    region: state.region,
  }));

  const { isCreatePage } = usePageType();
  const { open: openLoginDialog } = useGlobalModal('loginDialog');

  const [isSticky, setIsSticky] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [, setActivityPageFrom] = useSessionStorageState<ActivityPageFromEnum>(ACTIVITY_PAGE_FROM_KEY, {
    defaultValue: ActivityPageFromEnum.OUTSIDE,
  });

  const shouldShowAnonymousMobileMenu =
    (isMdScreen || isLenovoMini) &&
    !userInfo &&
    (pathname.startsWith('/home') ||
      pathname.startsWith('/explore') ||
      pathname.startsWith('/assets') ||
      pathname.startsWith('/project') ||
      pathname.startsWith('/create') ||
      pathname.startsWith('/tools') ||
      pathname.startsWith('/templates'));

  const bannerActivity = useMemo<HeaderActivityConfig | undefined>(() => {
    if (!region) {
      return undefined;
    }
    return HEADER_ACTIVITIES.find((activity) => shouldShowActivity(activity, region, locale));
  }, [locale, region]);

  useEffect(() => {
    if (!sticky) return;

    const handleScroll = () => {
      const nextIsSticky = window.scrollY > 0;
      setIsSticky((prevIsSticky) => (prevIsSticky === nextIsSticky ? prevIsSticky : nextIsSticky));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sticky]);

  return (
    <>
      <header
        id="header"
        data-google-interstitial="false"
        className={cn(
          'relative z-30 flex h-[80px] w-full items-center justify-between p-6 text-[#FFFFFF99] text-base data-[open=true]:bg-[rgba(19,12,20,0.8)] md:h-16 md:p-4',
          { 'fixed top-0 left-0': sticky && isSticky },
          className,
        )}
      >
        <div className="inline-flex items-center font-bold text-3xl hover:cursor-pointer sm:flex-1">
          {!isEnterpriseDistribution && (
            <ClientLink href={HOME_PAGE} aria-label="Vidu">
              <LoginWhite className="hidden text-3xl text-system-white md:block" />
            </ClientLink>
          )}
        </div>

        <div className={cn('flex items-center justify-end', isCreatePage && isMobile ? '' : 'flex-1')}>
          {bannerActivity && (
            <img
              src={getActivityImageUrl(bannerActivity, locale)}
              alt={bannerActivity.key}
              className="mr-4 h-10 cursor-pointer rounded-full object-contain sm:max-w-[120px]"
              onClick={() => {
                const redirectUrl = bannerActivity.getRedirectUrl?.(userInfo);
                if (redirectUrl) {
                  redirectUrl.url.includes('/activity') && setActivityPageFrom(ActivityPageFromEnum.INSIDE_BAR);
                  if (redirectUrl.target === '_blank') {
                    openOrRedirect(redirectUrl.url, { target: redirectUrl.target });
                  } else {
                    router.push(redirectUrl.url);
                  }
                }
              }}
            />
          )}

          {userInfo && <LoggedInHeaderSection />}
          {shouldShowAnonymousMobileMenu && (
            <button
              type="button"
              className="mr-3 flex size-9 items-center justify-center rounded-full bg-white/10 text-white"
              onClick={() => setShowMobileMenu(true)}
              aria-label="Open navigation menu"
            >
              <BaseLineList className="text-xl" />
            </button>
          )}
          {showLoginButton && (
            <button
              type="button"
              className="box-border inline-flex h-12 w-[168px] items-center justify-center rounded-full bg-ShengshuButton font-semibold text-base text-black hover:bg-ShengshuButtonHover"
              onClick={() => {
                openLoginDialog();
                reportEvent('login_pop_exposure', {
                  refer_element: 'header - sign in',
                  refer_page: pathname,
                });
              }}
            >
              {t('header.navLogin')}
            </button>
          )}
        </div>
      </header>
      {showMobileMenu && (
        <div className="fixed top-0 left-0 z-40 h-full w-full bg-[rgba(0,0,0,0.3)]">
          <MobileMenu onClose={() => setShowMobileMenu(false)} />
        </div>
      )}
      {sticky && isSticky && <div className="h-[72px] md:h-16" />}
    </>
  );
};
